import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { z } from 'zod';
import type { SignalsCountByAssetQuerySchema } from '@terra/shared-contracts-signals';
import { toSignalCountsByAssetIdRecord } from '@terra/ui-ai-applications';
import { terraProxyApiClient } from '../../clients/testerClient';

export const AI_COMPONENT_SIGNAL_COUNTS_KEY = 'ai-component-signal-counts';

const STALE_TIME_MS = 60_000;

export type AiComponentSignalCountsFilters = Omit<
  z.input<typeof SignalsCountByAssetQuerySchema>,
  'endpointIds'
>;

export function useAiComponentSignalCounts({
  applicationId,
  endpointIds,
  filters,
  enabled = true,
}: {
  applicationId: string | null;
  endpointIds?: string[];
  filters?: AiComponentSignalCountsFilters;
  enabled?: boolean;
}) {
  const normalizedEndpointIds = useMemo(
    () =>
      endpointIds && endpointIds.length > 0
        ? [...endpointIds].sort()
        : undefined,
    [endpointIds],
  );

  const query = useQuery({
    queryKey: [
      AI_COMPONENT_SIGNAL_COUNTS_KEY,
      applicationId,
      normalizedEndpointIds,
      filters,
    ],
    queryFn: async () => {
      if (!applicationId) {
        return { assetCounts: [], totalSignals: 0 };
      }

      return terraProxyApiClient.getSignalsCountByAssetId(applicationId, {
        ...filters,
        ...(normalizedEndpointIds && { endpointIds: normalizedEndpointIds }),
      });
    },
    enabled: Boolean(applicationId) && enabled,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  const signalCountsByComponentId = useMemo(
    () => toSignalCountsByAssetIdRecord(query.data?.assetCounts ?? []),
    [query.data?.assetCounts],
  );

  return {
    signalCountsByComponentId,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
